import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@formrails/environments';
import { ExistingFormElementDto, CreateFormElementBatchItemDto, CreateFormElementBatchRequestDto } from '@formrails/shared/data-access';

/**
 * CRUD access for form elements. These endpoints live on the dedicated
 * Form API (pmp-formapi-*) — application-template operations remain on the
 * Application API. Auth0 HTTP interceptor adds the Authorization header.
 */
@Injectable({
  providedIn: 'root'
})
export class FormElementApiService {
  private http = inject(HttpClient);
  private apiUrl = environment.formApi;

  /**
   * Fetch existing form elements
   */
  getExistingFormElements(): Observable<ExistingFormElementDto[]> {
    return this.http.get<ExistingFormElementDto[]>(
      `${this.apiUrl}/formelement`
    );
  }

  /**
   * Fetch a single form element by id
   */
  getFormElementById(id: string): Observable<ExistingFormElementDto> {
    return this.http.get<ExistingFormElementDto>(
      `${this.apiUrl}/formelement/${id}`
    );
  }

  /**
   * Create a single form element
   */
  createFormElement(formElement: CreateFormElementBatchItemDto): Observable<ExistingFormElementDto> {
    return this.http.post<ExistingFormElementDto>(
      `${this.apiUrl}/formelement`,
      formElement
    );
  }

  /**
   * Create form elements in batch via the /api/v1/formelement/batch endpoint.
   * Used for creating new reportable form elements that can be reused across forms.
   */
  createFormElementsBatch(formElements: CreateFormElementBatchItemDto[]): Observable<ExistingFormElementDto[]> {
    // The endpoint takes an object wrapping the array, not the array itself.
    const body: CreateFormElementBatchRequestDto = { formElements };

    return this.http.post<ExistingFormElementDto[]>(
      `${this.apiUrl}/formelement/batch`,
      body
    );
  }

  /**
   * Update an existing form element
   */
  updateFormElement(id: string, formElement: CreateFormElementBatchItemDto): Observable<ExistingFormElementDto> {
    return this.http.put<ExistingFormElementDto>(
      `${this.apiUrl}/formelement/${id}`,
      formElement
    );
  }
}
